// cspell:ignore DDTHH
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { TranslationFunction, TranslationPack } from '../../i18n';
import type { ExcludeStrict } from '../../types';
import { cap } from '../../utils';

import type { AbsoluteVariant, DateTimeFormat, DateTimeVariant } from './DateTime.types';
import { CALENDAR_TYPES, type CalendarType } from './Input/local';
import {
  getEndOfWeek,
  getQuarterForDateTimeZone,
  getStartOfWeek,
  getWeek,
  getWeekYear
} from './Input/utils';
import type { ClockFormat } from './Input/utils';

dayjs.extend(utc);
dayjs.extend(tz);

const unitsInMs = {
  year: 24 * 60 * 60 * 1000 * 365,
  month: (24 * 60 * 60 * 1000 * 365) / 12,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000
} satisfies Partial<Record<Intl.RelativeTimeFormatUnit, number>>;

type TimeUnit = keyof typeof unitsInMs;
const timeUnitsOrdered = [
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second'
] as const satisfies TimeUnit[];

type DateTimeFormatOptions = {
  locale: string;
  format: DateTimeFormat;
  timeZone?: string;
  clockFormat?: ClockFormat;
  calendarType?: CalendarType;
} & (
  | {
      variant: ExcludeStrict<DateTimeVariant, 'quarteryear'>;
      t?: TranslationFunction<TranslationPack>;
    }
  | { variant: 'quarteryear'; t: TranslationFunction<TranslationPack> }
);

type DateOptions = Pick<Intl.DateTimeFormatOptions, 'year' | 'month' | 'day'>;
type TimeOptions = Pick<Intl.DateTimeFormatOptions, 'hour' | 'minute' | 'second'>;
type DateStyle = Pick<Intl.DateTimeFormatOptions, 'dateStyle'>;
type TimeStyle = Pick<Intl.DateTimeFormatOptions, 'timeStyle'>;

const dateShort = {
  dateStyle: 'short'
} satisfies DateStyle;

const dateMedium = {
  dateStyle: 'medium'
} satisfies DateStyle;

const dateLong = {
  dateStyle: 'long'
} satisfies DateStyle;

const dateNumeric = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
} satisfies DateOptions;

const timeShort = {
  timeStyle: 'short'
} satisfies TimeStyle;

const timeMedium = {
  timeStyle: 'medium'
} satisfies TimeStyle;

const timeLong = {
  timeStyle: 'medium'
} satisfies TimeStyle;

const timeNumeric = {
  hour: 'numeric',
  minute: 'numeric'
} satisfies TimeOptions;

const dateTimeShort = {
  ...dateShort,
  ...timeShort
} satisfies DateStyle & TimeStyle;

const dateTimeMedium = {
  ...dateMedium,
  ...timeShort
} satisfies DateStyle & TimeStyle;

const dateTimeLong = {
  ...dateLong,
  ...timeMedium
} satisfies DateStyle & TimeStyle;

const dateTimeNumeric = {
  ...dateNumeric,
  ...timeNumeric
} satisfies DateOptions & TimeOptions;

const timeLongNumeric = {
  ...timeNumeric,
  second: 'numeric'
} satisfies TimeOptions;

const dateTimeLongNumeric = {
  ...dateNumeric,
  ...timeLongNumeric
} satisfies DateOptions & TimeOptions;

type AddClockFormatToTimeKeys<Key extends DateTimeVariant> = Key extends `${string}time`
  ? `${Key}${'' | 12 | 24}`
  : Key;

const formatMapping = {
  date: {
    short: dateShort,
    medium: dateMedium,
    long: dateLong,
    numeric: dateNumeric,
    'long-numeric': dateNumeric
  },
  datetime: {
    short: dateTimeShort,
    medium: dateTimeMedium,
    long: dateTimeLong,
    numeric: dateTimeNumeric,
    'long-numeric': dateTimeLongNumeric
  },
  datetime12: {
    short: { ...dateTimeShort, hour12: true },
    medium: { ...dateTimeMedium, hour12: true },
    long: { ...dateTimeLong, hour12: true },
    numeric: { ...dateTimeNumeric, hour12: true },
    'long-numeric': { ...dateTimeLongNumeric, hour12: true }
  },
  datetime24: {
    short: { ...dateTimeShort, hour12: false },
    medium: { ...dateTimeMedium, hour12: false },
    long: { ...dateTimeLong, hour12: false },
    numeric: { ...dateTimeNumeric, hour12: false },
    'long-numeric': { ...dateTimeLongNumeric, hour12: false }
  },
  time: {
    short: timeShort,
    medium: timeMedium,
    long: timeLong,
    numeric: timeNumeric,
    'long-numeric': timeLongNumeric
  },
  time12: {
    short: { ...timeShort, hour12: true },
    medium: { ...timeMedium, hour12: true },
    long: { ...timeLong, hour12: true },
    numeric: { ...timeNumeric, hour12: true },
    'long-numeric': { ...timeLongNumeric, hour12: true }
  },
  time24: {
    short: { ...timeShort, hour12: false },
    medium: { ...timeMedium, hour12: false },
    long: { ...timeLong, hour12: false },
    numeric: { ...timeNumeric, hour12: false },
    'long-numeric': { ...timeLongNumeric, hour12: false }
  },
  month: {
    short: { month: 'short' },
    medium: { month: 'short' },
    long: { month: 'long' },
    numeric: { month: 'numeric' },
    'long-numeric': { month: 'numeric' }
  },
  monthyear: {
    short: { year: 'numeric', month: 'short' },
    medium: { year: 'numeric', month: 'short' },
    long: { year: 'numeric', month: 'long' },
    numeric: { year: 'numeric', month: 'numeric' },
    'long-numeric': { year: 'numeric', month: 'numeric' }
  },
  week: {
    short: { month: 'short', day: 'numeric' },
    medium: { month: 'short', day: 'numeric' },
    long: { month: 'long', day: 'numeric' },
    numeric: { month: 'numeric', day: 'numeric' },
    'long-numeric': { month: 'numeric', day: 'numeric' }
  },
  year: {
    short: { year: 'numeric' },
    medium: { year: 'numeric' },
    long: { year: 'numeric' },
    numeric: { year: '2-digit' },
    'long-numeric': { year: '2-digit' }
  },
  relative: {
    short: { style: 'long', numeric: 'auto' },
    medium: { style: 'long', numeric: 'auto' },
    long: { style: 'long', numeric: 'auto' },
    numeric: { style: 'short', numeric: 'auto' },
    'long-numeric': { style: 'short', numeric: 'auto' }
  }
} satisfies Record<
  AddClockFormatToTimeKeys<ExcludeStrict<AbsoluteVariant, 'quarteryear'>>,
  Record<DateTimeFormat, Intl.DateTimeFormatOptions>
> &
  Record<'relative', Record<DateTimeFormat, Intl.RelativeTimeFormatOptions>>;

const dateTimeFormatCache: Map<
  // locale & stringified sorted Intl.DateTimeFormatOptions
  string,
  Intl.DateTimeFormat
> = new Map();

export function getIntlDateTimeFormatFromCache(
  locale: string,
  options: Intl.DateTimeFormatOptions = {}
) {
  const sortedOptions = JSON.stringify(options, Object.keys(options).sort());
  const key = `${locale}${sortedOptions}`;

  if (!dateTimeFormatCache.has(key)) {
    dateTimeFormatCache.set(key, new Intl.DateTimeFormat(locale, options));
  }

  return dateTimeFormatCache.get(key)!;
}

const relativeTimeFormatCache: Record<
  // locale
  string,
  Map<Intl.RelativeTimeFormatOptions, Intl.RelativeTimeFormat>
> = {};

const displayNamesCache: Record<
  // locale
  string,
  Intl.DateTimeDisplayNames
> = {};

export function formatDateTime(
  value: Date,
  {
    t,
    locale,
    format,
    variant,
    clockFormat,
    calendarType = CALENDAR_TYPES.ISO_8601,
    timeZone
  }: DateTimeFormatOptions
) {
  const getFormattedDate = (
    date: Date,
    dateVariant: ExcludeStrict<AbsoluteVariant, 'quarteryear'>
  ) => {
    const resolvedVariant: AddClockFormatToTimeKeys<typeof dateVariant> =
      dateVariant === 'time' || dateVariant === 'datetime'
        ? `${dateVariant}${clockFormat ?? ''}`
        : dateVariant;
    const resolvedOptions = formatMapping[resolvedVariant][format];

    return getIntlDateTimeFormatFromCache(locale, {
      ...resolvedOptions,
      timeZone
    }).format(date);
  };

  const relativeTimeFormatter = (
    relativeValue: number,
    unit: TimeUnit,
    enforceNumeric?: boolean
  ) => {
    const resolvedOptions: Intl.RelativeTimeFormatOptions = {
      ...formatMapping.relative[format],
      numeric: enforceNumeric ? 'always' : 'auto'
    };

    relativeTimeFormatCache[locale] ??= new Map();
    const map = relativeTimeFormatCache[locale];

    if (!map.has(resolvedOptions)) {
      map.set(
        resolvedOptions,
        new Intl.RelativeTimeFormat(locale, {
          ...resolvedOptions
        })
      );
    }

    return map.get(resolvedOptions)!.format(relativeValue, unit);
  };

  const getRelativeTime = (date: Date) => {
    const now = dayjs();
    const targetDate = dayjs(date);

    // Deliberately reduce precision to 1 minute, handles "now" cases
    const isWithinMinute = Math.abs(targetDate.diff(now, 'millisecond')) < 60000;
    if (isWithinMinute) {
      return relativeTimeFormatter(0, 'second', false);
    }

    // Find the most appropriate unit by checking dayjs diff for each unit
    for (const unit of timeUnitsOrdered) {
      const diff = targetDate.diff(now, unit);
      if (Math.abs(diff) >= 1) {
        return relativeTimeFormatter(diff, unit, true);
      }
    }

    // Fallback to seconds if no unit has a difference >= 1
    const secondsDiff = targetDate.diff(now, 'second');
    return relativeTimeFormatter(secondsDiff, 'second', true);
  };

  const getFormattedWeek = (date: Date) => {
    displayNamesCache[locale] ??= new Intl.DisplayNames(locale, {
      style: 'long',
      type: 'dateTimeField'
    }) as Intl.DateTimeDisplayNames;

    const displayNames = displayNamesCache[locale];
    const startDate = getFormattedDate(getStartOfWeek(date, calendarType, timeZone), 'week');
    const endDate = getFormattedDate(getEndOfWeek(date, calendarType, timeZone), 'week');

    return `${cap(displayNames.of('weekOfYear'))} ${getWeek(
      date,
      calendarType,
      timeZone
    )}, ${getWeekYear(date, calendarType, timeZone)} (${startDate} - ${endDate})`;
  };

  const getFormattedQuarterYear = (date: Date) => {
    const formattedYear = getFormattedDate(date, 'year');
    const quarterNumber = getQuarterForDateTimeZone(date, timeZone);
    return `${t!(`date_quarter_q${quarterNumber}`, [formattedYear])}`;
  };

  switch (variant) {
    case 'relative':
      return getRelativeTime(value);
    case 'week':
      return getFormattedWeek(value);
    case 'quarteryear':
      return getFormattedQuarterYear(value);
    default:
      return getFormattedDate(value, variant);
  }
}

const dayInMs = 24 * 60 * 60 * 1000;
const hourInMs = 60 * 60 * 1000;
const minuteInMs = 60 * 1000;
const secondInMs = 1000;

interface DurationFormatOptions {
  /** Locale to be used - en_US, jp_JP, fr_FR etc */
  locale: string;
  /** Determines maximum number of units to be displayed. */
  significantUnits: 5 | 4 | 3 | 2 | 1;
  /**
   * Unit formatting style
   * @default "narrow"
   */
  unitDisplay?: Intl.NumberFormatOptions['unitDisplay'];
  /**
   * Unit of maximum precision
   * @default "second"
   */
  maxPrecision?: 'day' | 'hour' | 'minute' | 'second' | 'millisecond';
}

const numberFormatCache: Record<
  // locale
  string,
  Record<
    // unit
    string,
    Record<
      // unitDisplay
      string,
      Intl.NumberFormat
    >
  >
> = {};

export function formatDuration(
  valueInMs: number,
  {
    locale,
    significantUnits,
    unitDisplay = 'narrow',
    maxPrecision = 'second'
  }: DurationFormatOptions
) {
  numberFormatCache[locale] ??= {};

  const sign = valueInMs < 0 ? '-' : '';
  const absMs = Math.abs(valueInMs);

  const durationParts = [
    { unit: 'day', value: Math.floor(absMs / dayInMs) },
    { unit: 'hour', value: Math.floor((absMs % dayInMs) / hourInMs) },
    { unit: 'minute', value: Math.floor((absMs % hourInMs) / minuteInMs) },
    { unit: 'second', value: Math.floor((absMs % minuteInMs) / secondInMs) },
    { unit: 'millisecond', value: absMs % secondInMs }
  ];

  const result = [];
  for (let i = 0; result.length < significantUnits && i < durationParts.length; i += 1) {
    const { value, unit } = durationParts[i];

    if (value > 0 || (unit === maxPrecision && result.length === 0)) {
      numberFormatCache[locale][unit] ??= {};
      numberFormatCache[locale][unit][unitDisplay] ??= new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay
      });

      result.push(numberFormatCache[locale][unit][unitDisplay].format(value));
    }
    if (durationParts[i].unit === maxPrecision) break;
  }
  return `${sign}${result.join(' ')}`;
}

/**
 * @returns Current date and time value adjusted for local time zone.
 */
export const getAdjustedUTCDateTime = (): Date => {
  const now = new Date();
  now.setTime(now.getTime() - now.getTimezoneOffset() * 60000);
  return now;
};

/**
 * @param timeZone - IANA timezone string (e.g. "America/New_York")
 * @returns Current date and time for the given timezone, with wall-clock time
 *          encoded in UTC fields — matching the convention used by DateTime inputs.
 */
export const getAdjustedUTCDateTimeForTimezone = (timeZone: string): Date => {
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-US', { timeZone });
  } catch {
    return getAdjustedUTCDateTime();
  }
  return new Date(`${dayjs().tz(timeZone).format('YYYY-MM-DDTHH:mm:ss.SSS')}Z`);
};
